import { z } from 'zod';

export const CreateContextSnapshotRequest = z.object({
  viewer_user_id: z.string().min(1).max(40),
  decision_id: z.string().min(1).max(40).optional(),
  purpose: z.enum(['chain_planning', 'action_support', 'risk_review', 'verification']),
  query_text: z.string().min(1).max(1000),
  risk_class: z.enum(['low', 'moderate', 'high']),
  max_candidates: z.number().int().min(1).max(30).default(12),
});
export const RecordContextUseRequest = z.object({
  candidate_id: z.string().min(1).max(40),
  decision_id: z.string().min(1).max(40),
  effect_kind: z.enum([
    'outcome',
    'sequence',
    'gap',
    'risk',
    'timing',
    'rendering',
    'verification',
    'obligation',
  ]),
  explanation: z.string().min(1).max(500),
});
export type CreateContextSnapshot = z.infer<typeof CreateContextSnapshotRequest>;
export type RecordContextUse = z.infer<typeof RecordContextUseRequest>;

export const ProposeContextCandidateRequest = z.object({
  source_snapshot_candidate_id: z.string().min(1).max(40).optional(),
  statement: z.string().min(1).max(1000),
  truth_class: z.enum(['observed_pattern', 'inference_candidate']),
  sensitivity: z.enum(['ordinary', 'personal', 'sensitive', 'high_risk']),
  visible_to_user_ids: z.array(z.string().min(1).max(40)).max(50).default([]),
  evidence_refs: z
    .array(
      z.object({
        source_type: z.string().min(1).max(80),
        source_id: z.string().min(1).max(80),
        summary: z.string().min(1).max(300),
      }),
    )
    .min(1)
    .max(20),
  confidence: z.number().min(0).max(1).optional(),
  expires_at: z.string().datetime().optional(),
});
export const ContextCandidateActionRequest = z.object({
  expected_version: z.number().int().positive(),
  action: z.enum(['confirm', 'reject', 'use_once', 'defer', 'not_anymore', 'expire']),
  reason_code: z
    .string()
    .min(2)
    .max(80)
    .regex(/^[a-z0-9_]+$/),
  maintenance_duration_ms: z.number().int().min(0).max(3_600_000).optional(),
});
export const SupersedeContextCandidateRequest = z.object({
  expected_version: z.number().int().positive(),
  reason_code: z
    .string()
    .min(2)
    .max(80)
    .regex(/^[a-z0-9_]+$/),
  replacement: ProposeContextCandidateRequest,
  maintenance_duration_ms: z.number().int().min(0).max(3_600_000).optional(),
});
export const RecordContextEffectRequest = z.object({
  snapshot_id: z.string().min(1).max(40),
  chain_planner_run_id: z.string().min(1).max(40).optional(),
  decision_id: z.string().min(1).max(40),
  baseline_snapshot: z.record(z.string(), z.unknown()),
  contextual_snapshot: z.record(z.string(), z.unknown()),
  correction_delta: z.number().int().optional(),
  context_helped: z.boolean().optional(),
  stale_harm: z.boolean().default(false),
  unauthorized_disclosure: z.boolean().default(false),
});
export const PromoteContextCandidateRequest = z.object({
  expected_version: z.number().int().positive(),
  category: z.enum([
    'cleaning_standards',
    'shopping_preferences',
    'car_rules',
    'guest_handling',
    'deliveries',
    'emergency_procedures',
    'contacts',
    'other',
  ]),
  title: z.string().min(1).max(200),
  scope: z.enum(['household', 'worker', 'general_practice']),
  effective_from: z.string().datetime({ offset: true }),
  reason_code: z
    .string()
    .regex(/^[a-z0-9_]+$/)
    .max(80),
});
export type ProposeContextCandidate = z.infer<typeof ProposeContextCandidateRequest>;
export type ContextCandidateAction = z.infer<typeof ContextCandidateActionRequest>;
export type SupersedeContextCandidate = z.infer<typeof SupersedeContextCandidateRequest>;
export type RecordContextEffect = z.infer<typeof RecordContextEffectRequest>;
export type PromoteContextCandidate = z.infer<typeof PromoteContextCandidateRequest>;
